import React, {useState, useMemo, Children, isValidElement, cloneElement} from 'react';
import {usePolling} from '../../utils/hooks';
import Modal from '../Modal/Modal';
import Button from '../Form/Button/Button';
import ButtonGroup from '../Form/Button/ButtonGroup';

/** @import {InactivityTimeoutProps, TimeoutCountdownModalProps} from './InactivityTimeoutProps' */

import './inactivityTimeout.scss';

function InactivityTimeout(/** @type {InactivityTimeoutProps} */ props) {
  const inactivityPeriodSeconds = 60 * 4;
  const timeoutCountdownSeconds = 60; 

  const [lastActivity, setLastActivity] = useState(new Date());
  const [secondsSinceLastMove, setSecondsSinceLastMove] = useState(0);
  const [showInactiveModal, setShowInactiveModal] = useState(false);

  const getTimeSinceLastMove = () => {
    return Math.floor((new Date().getTime() - lastActivity.getTime()) / 1000);
  }

  // when the user has been inactive for 60s show the inactive modal
  usePolling(() => {
    if (!props.user || props.user.isPending || !props.user.isAuthenticated)
      return;

    setSecondsSinceLastMove(getTimeSinceLastMove());
    if (getTimeSinceLastMove() > inactivityPeriodSeconds && !showInactiveModal) {
      setShowInactiveModal(true);
    }
  }, [lastActivity, props.user], 1000);

  const onActivity = (/** @type {React.MouseEvent<HTMLDivElement, MouseEvent> | React.KeyboardEvent<HTMLDivElement>} */ _e) => {
    setLastActivity(new Date());
  };

  const logoutTriggered = () => {
    setShowInactiveModal(false);
    props.onLogout();
  };

  const timeoutTriggered = () => {
    setShowInactiveModal(false);
    props.onInactive();
  };

  const childrenWithProps = Children.map(props.children, child => {
    // Checking isValidElement is the safe way and avoids a TS error too.
    if (isValidElement(child)) {
      return cloneElement(child, { ...props })
    }

    return child;
  });

  const mainArea = useMemo( () => {
    return (<div onMouseMove={onActivity} onClick={onActivity} onKeyDown={onActivity}>
      {showInactiveModal &&
          <TimeoutCountdownModal
              timeoutCountdownSeconds={timeoutCountdownSeconds}
              logout={logoutTriggered}
              cancel={() => setShowInactiveModal(false)}
              timeout={timeoutTriggered} />
      }
      {childrenWithProps}
    </div>)
  }, [showInactiveModal, timeoutCountdownSeconds] );
  
  return (
    <>
      {props.debug && 
        <div>
          Last move/action: {lastActivity.toString()}
          Seconds since last move/action: {secondsSinceLastMove}
        </div>
      }

      {mainArea}
    </>
  );
};

function TimeoutCountdownModal(/** @type {TimeoutCountdownModalProps} */ {timeoutCountdownSeconds, logout, cancel, timeout}) {
  const [countdownSeconds, setCountdownSeconds] = useState(timeoutCountdownSeconds);

  usePolling(() => {
    if (countdownSeconds <= 0) {
      timeout();
    }
    else {
      setCountdownSeconds(countdownSeconds - 1);    
    }
  }, [countdownSeconds], 1000);

  return (
    <Modal onClose={() => cancel()} className="modal--fixed-height inactivity-modal" ignoreClickOutside={true}>
      <h2 className="modal__title login__title otp-modal__title">Still there?</h2>
      <p>We have noticed you have been inactive for a while, so we will log you out in {countdownSeconds} second{countdownSeconds == 1 ? "" : "s"}.</p>
      <div className='modal__button-group'>
        <ButtonGroup>
            <Button size='med' label='Stay logged in' onClick={cancel} />
            <Button variant='secondary' size='med' label='Log out now' onClick={logout} />
        </ButtonGroup>
      </div>
    </Modal>
  );
};

export default InactivityTimeout;